import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr, dateStr } from "@/lib/format";
import { KPI, SectionHeader } from "@/components/ui-extras";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  Briefcase, Wallet, TrendingUp, AlertTriangle, IndianRupee, Users, Target, Receipt, Sparkles
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import { toast } from "sonner";

const COLORS = ["#1A3636", "#D4A373", "#E2B872", "#5F8B7C", "#A47148"];

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [aging, setAging] = useState(null);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    try {
      const [k, a, p, i] = await Promise.all([
        api.get("/mis/summary"),
        api.get("/mis/aging"),
        api.get("/projects"),
        api.get("/invoices"),
      ]);
      setKpis(k.data); setAging(a.data); setProjects(p.data); setInvoices(i.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const reseed = async () => {
    setSeeding(true);
    try {
      await api.post("/admin/seed?reset=true");
      toast.success("Sample data re-seeded");
      await load();
    } catch (e) {
      toast.error("Seeding failed");
    } finally {
      setSeeding(false);
    }
  };

  const agingRows = aging
    ? [
        { bucket: "Current", value: aging.current },
        { bucket: "0-30", value: aging["0-30"] },
        { bucket: "31-60", value: aging["31-60"] },
        { bucket: "61-90", value: aging["61-90"] },
        { bucket: "90+", value: aging["90+"] },
      ]
    : [];

  const projectBarData = projects.slice(0, 6).map((p) => ({
    name: p.code,
    Planned: p.planned_progress_pct,
    Actual: p.actual_progress_pct,
  }));

  const invoicePie = [
    { name: "Collected", value: kpis?.total_collected || 0 },
    { name: "Outstanding", value: kpis?.total_outstanding || 0 },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      <SectionHeader
        title="Company overview"
        subtitle="Live snapshot across finance, projects, HR and sales"
        right={
          <Button
            variant="outline"
            onClick={reseed}
            disabled={seeding}
            data-testid="reseed-btn"
            className="h-9"
          >
            {seeding ? "Seeding…" : "Reset sample data"}
          </Button>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI testId="kpi-contract" label="Total Contract Value" value={kpis?.total_contract_value ?? 0} compact icon={Briefcase} />
        <KPI testId="kpi-billed" label="Billed Y-T-D" value={kpis?.total_billed ?? 0} compact icon={Receipt} />
        <KPI testId="kpi-collected" label="Collected" value={kpis?.total_collected ?? 0} compact icon={IndianRupee} tone="good" />
        <KPI testId="kpi-overdue" label="Overdue" value={kpis?.overdue_amount ?? 0} compact icon={AlertTriangle} tone={kpis?.overdue_amount > 0 ? "bad" : "default"} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI testId="kpi-margin" label="Gross Margin" value={kpis?.gross_margin ?? 0} compact icon={TrendingUp} tone={kpis?.gross_margin >= 0 ? "good" : "bad"} />
        <KPI testId="kpi-projects" label="Active Projects" value={kpis?.active_projects ?? 0} suffix={` / ${kpis?.total_projects ?? 0}`} icon={Briefcase} />
        <KPI testId="kpi-employees" label="Employees" value={kpis?.employees ?? 0} icon={Users} />
        <KPI testId="kpi-pipeline" label="Sales Pipeline" value={kpis?.pipeline_value ?? 0} compact icon={Target} tone="warn" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold font-display flex items-center justify-between">
              Project progress · Planned vs Actual
              <Badge variant="outline" className="font-mono text-[10px]">% complete</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-[260px]">
              <ResponsiveContainer>
                <BarChart data={projectBarData} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Planned" fill="#D4A373" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Actual" fill="#1A3636" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold font-display">Billing status</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-[220px]">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={invoicePie} innerRadius={50} outerRadius={80} dataKey="value" stroke="none">
                    {invoicePie.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => inr(v, { compact: true })} contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
              <div className="flex items-center gap-1.5"><span className="h-2 w-2 bg-primary rounded-sm"/>Collected · {inr(kpis?.total_collected ?? 0, { compact: true })}</div>
              <div className="flex items-center gap-1.5"><span className="h-2 w-2 bg-secondary rounded-sm"/>Outstanding · {inr(kpis?.total_outstanding ?? 0, { compact: true })}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Aging + Recent invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold font-display">Receivables aging</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            {agingRows.map((r) => (
              <div key={r.bucket} className="flex items-center gap-3">
                <div className="w-20 text-xs text-muted-foreground font-mono">{r.bucket}</div>
                <Progress
                  value={aging ? Math.min(100, (r.value / Math.max(1, Math.max(...agingRows.map(a => a.value)))) * 100) : 0}
                  className="h-2 flex-1"
                />
                <div className="w-24 text-right text-sm font-medium cell-num">{inr(r.value, { compact: true })}</div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold font-display">Recent invoices</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                    <th className="py-2 font-medium">Invoice</th>
                    <th className="py-2 font-medium">Client</th>
                    <th className="py-2 font-medium">Due</th>
                    <th className="py-2 font-medium text-right">Amount</th>
                    <th className="py-2 font-medium text-right">Outstanding</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.slice(0, 8).map((i) => (
                    <tr key={i.invoice_id} className="border-b hover:bg-accent/50">
                      <td className="py-2 font-mono text-xs">{i.invoice_no}</td>
                      <td className="py-2 truncate max-w-[160px]">{i.client}</td>
                      <td className="py-2 text-xs">{dateStr(i.due_date)}</td>
                      <td className="py-2 cell-num">{inr(i.amount, { compact: true })}</td>
                      <td className="py-2 cell-num">{inr(i.outstanding, { compact: true })}</td>
                      <td className="py-2">
                        <Badge
                          variant="outline"
                          className={
                            i.status === "paid" ? "border-emerald-600 text-emerald-700 bg-emerald-50" :
                            i.is_overdue ? "border-destructive text-destructive bg-red-50" :
                            i.status === "partial" ? "border-amber-600 text-amber-700 bg-amber-50" :
                            "border-border"
                          }
                        >
                          {i.is_overdue ? `Overdue ${i.days_overdue}d` : i.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-none border-secondary/40 bg-secondary/5">
        <CardContent className="p-4 flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-secondary shrink-0" />
          <div className="text-sm">
            <strong className="font-medium">Tip:</strong> Open the AI Assistant (top-right) to ask
            things like <em>&ldquo;Which project has the worst CPI?&rdquo;</em> or <em>&ldquo;Total salary payout this month?&rdquo;</em>.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
